<template>
  <TemplateHead :config="config" />

  <div :class="fonts[config.style.font]">
    <div
      class="w-full flex justify-center shadow-md"
      :style="{
        backgroundColor: `#${primaryColor}`,
      }"
    >
      <div class="w-[80rem] py-2 text-white text-lg font-bold">
        <img
          v-if="config.header?.logo_url"
          class="h-8 object-contain"
          :src="config.header?.logo_url"
          alt="Logo"
        >
        <img
          v-else-if="isPreview()"
          class="h-8 object-contain"
          src="https://placehold.co/140x30"
          alt="Logo"
        >
      </div>
    </div>

    <div class="w-full relative">
      <div
        class="absolute h-[28rem] w-full z-[-1]"
        :style="{
          backgroundColor: `#${primaryColor}`,
        }"
      />
      <div
        class="absolute h-full w-full z-[-2]"
        :style="{
          backgroundColor: `#${ config.style?.body}`,
        }"
      />

      <div class="flex justify-center z-10 p-4 pt-8">
        <div class="w-[80rem]">
          <div class="text-center justify-center">
            <div class="text-5xl font-bold text-center text-white">
              {{ config.body.header_title }}
            </div>

            <div class="text-white text-2xl text-center">
              {{ config.body.header_subtitle }}
            </div>
          </div>

          <div class="pt-8">
            <div
              class="bg-white"
              :style="{
                'box-shadow': `0px 0px 5px #777`
              }"
            >
              <div class="w-full bg-gray-100">
                <div class="p-4">
                  <div class="text-center text-xs">
                    {{ __('label_progress') }} ({{ activeStepIndex }}/{{ stepCount }})
                  </div>

                  <div class="flex justify-center">
                    <div
                      class="w-3/4 bg-white grid"
                      :style="{
                        gridTemplateColumns: `repeat(${stepCount}, 1fr)`,
                      }"
                    >
                      <div
                        v-for="index in [...Array(stepCount).keys()]"
                        :key="index"
                      >
                        <div
                          class="w-full p-2"
                          :style="{
                            backgroundColor: activeStepIndex > index ? '#' + progressBarColor : '#ffffff',
                          }"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="h-full">
                <div class="px-4 sm:px-16 lg:px-32 py-12">
                  <div v-if="activeStepIndex !== stepCount">
                    <p class="text-2xl font-semibold">
                      {{ activeStep?.title }}
                    </p>

                    <div
                      v-for="(option, index) in activeStep?.options"
                      :key="index"
                      class="pt-8"
                    >
                      <div class="inline-flex align-middle">
                        <div
                          class="rounded-full w-12 h-12 flex justify-center items-center border-gray-200 border-2 cursor-pointer hover:border-gray-300 break-all"
                          @click="answerQuestion(option)"
                        />


                        <p class="my-auto ml-4 text-lg">
                          {{ option.title }}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div v-else>
                    <div class="text-center grid space-y-6">
                      <div
                        v-if="customOutImageUrl"
                        class="flex justify-center"
                      >
                        <img
                          class="w-[11rem] h-[11rem] object-contain"
                          :src="customOutImageUrl"
                          alt="Image"
                        >
                      </div>
                      <i
                        v-else
                        class="far fa-check-circle text-8xl text-gray-400"
                      />

                      <p class="text-2xl">
                        {{ outTitle }}
                      </p>

                      <div class="flex justify-center">
                        <a
                          class="text-center border-lime-500 border-2 text-lime-500 rounded-sm text-xl font-semibold flex items-center justify-center cursor-pointer py-4 px-8"
                          :href="outUrl"
                        >
                          {{ outButtonText }}
                        </a>
                      </div>

                      <p class="text-sm text-gray-400">
                        {{ outText }}
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  v-if="(quizConfig?.show_navigation ?? '0') !== '0'"
                  class="w-full bg-gray-100 flex justify-center"
                >
                  <div class="grid grid-cols-2 w-1/2 lg:w-1/4 text-center gap-2 p-4">
                    <div
                      class="rounded-md p-2 text-white hover:bg-gray-300"
                      :class="{
                        'cursor-not-allowed bg-gray-300': !canGoPreviousStep,
                        'bg-gray-400 cursor-pointer': canGoPreviousStep,
                      }"
                      @click="previousStep"
                    >
                      <i class="far fa-chevron-left text-sm" /> <span v-if="!isMobile">{{ __('label_back') }}</span>
                    </div>

                    <div
                      class="rounded-md p-2 text-white hover:bg-gray-300"
                      :class="{
                        'cursor-not-allowed bg-gray-300': !canGoNextStep,
                        'bg-gray-400 cursor-pointer': canGoNextStep,
                      }"
                      @click="nextStep"
                    >
                      <span v-if="!isMobile">{{ __('label_next') }}</span> <i class="far fa-chevron-right text-sm" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="flex justify-center"
      :style="{
        backgroundColor: `#${ config.style?.body}`,
      }"
    >
      <div class="w-[80rem]">
        <div class="p-4 pt-16">
          <img
            class="w-full"
            src="@timon/js/Templates/PRE-LANDER-002/assets/divider.png"
            alt="Divider"
          >
        </div>

        <div class="px-4 py-16">
          <p class="text-center text-4xl font-bold">
            {{ config.body.about_title }}
          </p>

          <!-- eslint-disable vue/no-v-html -->
          <div
            class="text-center p-2 text-lg"
            v-html="config.body.about"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>

        <div
          v-if="(!!config.body.faq_enabled) && (config.body.faq_enabled !== '0')"
          class="p-4"
        >
          <img
            class="w-full"
            src="@timon/js/Templates/PRE-LANDER-002/assets/divider.png"
            alt="Divider"
          >
        </div>

        <div
          v-if="(!!config.body.faq_enabled) && (config.body.faq_enabled !== '0')"
          class="px-4 pb-4 pt-16"
        >
          <p class="text-center text-4xl font-semibold">
            {{ config.body.faq_title }}
          </p>

          <p class="text-center text-gray-500">
            {{ config.body.faq_subtitle }}
          </p>

          <div
            class="grid gap-6 py-16 gap-y-8"
            :class="{
              'grid-cols-1': config.body?.faq_columns === '1' || isMobile,
              'grid-cols-2': config.body?.faq_columns === '2' && !isMobile,
              'grid-cols-3': config.body?.faq_columns === '3' && !isMobile,
            }"
          >
            <div
              v-for="(item, key) in config.body.faq ?? []"
              :key="key"
            >
              <div class="flex py-2">
                <i class="fas fa-question-circle text-3xl text-gray-400 mt-2" />

                <p class="my-auto ml-4 text-xl">
                  {{ item.question }}
                </p>
              </div>

              <div class="ml-12">
                <!-- eslint-disable vue/no-v-html -->
                <div
                  class="text-gray-500"
                  v-html="item.answer"
                />
                <!-- eslint-enable vue/no-v-html -->
              </div>
            </div>
          </div>
        </div>

        <div class="p-4">
          <img
            class="w-full"
            src="@timon/js/Templates/PRE-LANDER-002/assets/divider.png"
            alt="Divider"
          >
        </div>
      </div>
    </div>

    <div
      class="p-4 flex justify-center text-white"
      :style="{
        backgroundColor: `#${primaryColor}`,
      }"
    >
      <div class="w-[80rem]">
        <div class="text-xl">
          {{ config.footer.title }}
        </div>

        <!-- eslint-disable vue/no-v-html -->
        <div
          class="py-2 text-sm"
          v-html="config.footer?.contact_info"
        />
        <!-- eslint-enable vue/no-v-html -->

        <div class="grid gap-y-2 text-sm">
          <div
            v-if="config.footer?.facebook_name"
            class="inline-flex"
          >
            <img
              class="w-5 h-5 object-contain mr-2"
              src="@timon/js/Templates/PRE-LANDER-002/assets/icon-facebook.svg"
              alt="Facebook"
            >

            <p class="mr-1">
              {{ __('label_facebook') }}:
            </p>

            <a
              v-if="config.footer?.facebook_link"
              :href="config.footer?.facebook_link"
              class="underline text-lime-200"
              target="_blank"
            >
              {{ config.footer.facebook_name }}
            </a>
            <p v-else>
              {{ config.footer.facebook_name }}
            </p>
          </div>

          <div
            v-if="config.footer?.messenger_name"
            class="inline-flex"
          >
            <img
              class="w-5 h-5 object-contain mr-2"
              src="@timon/js/Templates/PRE-LANDER-002/assets/icon-messenger.svg"
              alt="Messenger"
            >

            <p class="mr-1">
              {{ __('label_messenger') }}:
            </p>

            <a
              v-if="config.footer?.messenger_link"
              :href="config.footer?.messenger_link"
              class="underline text-lime-200"
              target="_blank"
            >
              {{ config.footer.messenger_name }}
            </a>
            <p v-else>
              {{ config.footer.messenger_name }}
            </p>
          </div>

          <div
            v-if="config.footer?.email"
            class="inline-flex"
          >
            <img
              class="w-5 h-5 object-contain mr-2"
              src="@timon/js/Templates/PRE-LANDER-002/assets/icon-mail.svg"
              alt="Mail"
            >

            <p class="mr-1">
              {{ __('label_email') }}:
            </p>

            <a
              :href="`mailto:${config.footer.email}`"
              target="_blank"
              class="underline"
              :style="{
                color: `#${config.style.footer_link}`
              }"
            >
              {{ config.footer.email }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <Footer
      :pages="pages"
      :collector-flow-uuid="collectorFlowUuid"
      :config="config"
    />
  </div>
</template>

<script setup>
import {computed, defineProps, onMounted, onUnmounted, ref} from 'vue';
import Footer from '@timon/js/Templates/PRE-LANDER-002/Components/Footer.vue';
import {
  __,
  checkIfTargetingCookiesAreAccepted,
  clickoutParameters,
  isPreview,
  registerVisit,
} from '@timon/js/Mixins/helpers';
import TemplateHead from '@timon/js/Components/TemplateHead.vue';

const props = defineProps({
  config: {
    type: Object,
    required: true,
  },

  quizConfig: {
    type: Object,
    required: true,
  },


  variables: {
    type: Object,
    required: true,
  },

  collectorFlowUuid: {
    type: String,
    required: true,
  },

  pages: {
    type: Array,
    required: true,
  },

  adUrlUuid: {
    type: String,
    required: false,
    default: null,
  },
});

const fonts = {
  'mono': 'font-mono',
  'sans': 'font-sans',
  'serif': 'font-serif',
};

const stepCount = ref((props.quizConfig?.steps?.length ?? 0) + 1);
const activeStepIndex = ref(1);

const outTitle = ref(props.quizConfig?.out_title ?? __('pre_lander_002_out_title'));
const outText = ref(props.quizConfig?.out_text ?? __('pre_lander_002_out_text'));
const outUrl = ref('#');
const customOutImageUrl = ref(props.quizConfig?.custom_out_image_url ?? null);
const outButtonText = ref(__('pre_lander_002_out_button_text'));

const primaryColor = ref(props.config.style?.primary);

const progressBarColor = ref(props.config.style?.progress_bar);

const canGoPreviousStep = computed(() => activeStepIndex.value > 1 && activeStepIndex.value !== (stepCount.value));
const canGoNextStep = computed(() => activeStepIndex.value < stepCount.value);

const isMobile = ref(false);

const landingVisitUuid = ref(null);

function updateIsMobile() {
  isMobile.value = window.innerWidth < 768;
}
function acceptedCookies() {
  checkIfTargetingCookiesAreAccepted(() => registerVisit(props.adUrlUuid));
}

onMounted(() => {
  updateIsMobile();

  window.addEventListener('resize', updateIsMobile);
  window.addEventListener('CookieScriptAcceptAll', acceptedCookies);
  window.addEventListener('CookieScriptAccept', acceptedCookies);

  if (!isPreview()) {
    registerVisit(props.adUrlUuid, (data) => {
      landingVisitUuid.value = data.visit_key;

      outUrl.value = route('zazu.v1.clickout', {
        uuid: landingVisitUuid.value,
        ...clickoutParameters(),
      });
    });
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', updateIsMobile);
  window.removeEventListener('CookieScriptAcceptAll', acceptedCookies);
  window.removeEventListener('CookieScriptAccept', acceptedCookies);
});

function nextStep() {
  if (!canGoNextStep.value) {
    return;
  }

  activeStepIndex.value += 1;
}

function previousStep() {
  if (!canGoPreviousStep.value) {
    return;
  }

  activeStepIndex.value -= 1;
}

function answerQuestion(answer) {
  if (!answer.links_out || answer.links_out === '0') {
    nextStep();

    return;
  }

  outTitle.value = answer.out_title;
  outText.value = answer.out_text;
  outButtonText.value = answer.out_button_text;
  if (!isPreview()) {
    outUrl.value = route('zazu.v1.clickout', {
      uuid: landingVisitUuid.value,
      adUrl: answer.ad_url_uuid,
      ...clickoutParameters(),
    });
  }

  if (answer.out_image_url) {
    customOutImageUrl.value = answer.out_image_url;
  }

  activeStepIndex.value = stepCount.value;
}

const activeStep = computed(() => props.quizConfig?.steps[activeStepIndex.value - 1] ?? null);
</script>
